// Minimal semver utilities for OTA/native compatibility checks. Kept local
// (no npm dep) because the surface is tiny — just parse "x.y.z" and compare
// majors — and because both Capgo and Trapeze already pin their semver-ish
// handling to the same simple shape.

export type SemverTuple = [number, number, number]

export const parseSemver = (raw: string | undefined | null): SemverTuple | null => {
  if (!raw) return null
  // Accept "1.2.3" or "1.2.3-foo" or "1.2.3+build.1". Strip pre-release / build
  // metadata since we only care about numeric segments for compatibility.
  const [core] = raw.split(/[-+]/)
  const parts = core.split(".")
  if (parts.length < 2) return null
  const major = Number.parseInt(parts[0], 10)
  const minor = Number.parseInt(parts[1], 10)
  const patch = parts[2] === undefined ? 0 : Number.parseInt(parts[2], 10)
  if (!Number.isFinite(major) || !Number.isFinite(minor) || !Number.isFinite(patch)) {
    return null
  }
  return [major, minor, patch]
}

// Compatibility policy is strict: OTA bundle and native must match on major
// AND minor. Only patch differences are allowed. This makes every native
// release (`npm run bump:native`, which bumps minor) a natural barrier —
// users on the previous minor stop receiving OTAs until they update the
// native binary, so we never ship JS built against plugins/config that the
// user's native doesn't have. Returns false if either version can't be
// parsed — safer to skip an unparseable update than to apply blind.
export const isCompatible = (
  otaVersion: string | undefined | null,
  nativeVersion: string | undefined | null,
): boolean => {
  const ota = parseSemver(otaVersion)
  const native = parseSemver(nativeVersion)
  if (!ota || !native) return false
  return ota[0] === native[0] && ota[1] === native[1]
}

// Tuple-wise numeric comparison of two version strings.
// Returns -1 if a < b, 0 if equal, 1 if a > b.
// Returns null if either string can't be parsed.
export const compareSemver = (
  a: string | undefined | null,
  b: string | undefined | null,
): number | null => {
  const parsedA = parseSemver(a)
  const parsedB = parseSemver(b)
  if (!parsedA || !parsedB) return null
  for (let i = 0; i < 3; i++) {
    if (parsedA[i] < parsedB[i]) return -1
    if (parsedA[i] > parsedB[i]) return 1
  }
  return 0
}
